import React from 'react';
import { Chip, Box } from '@mui/material';

interface CallStatusBadgeProps {
  status?: string;
  resolved?: boolean;
  urgencyLevel?: string;
  callbackRequested?: boolean;
  callbackStatus?: string;
  humanTransferRequested?: boolean;
  escalationRequested?: boolean;
  under30Seconds?: boolean;
}

const CallStatusBadge: React.FC<CallStatusBadgeProps> = ({
  status,
  resolved,
  urgencyLevel,
  callbackRequested,
  callbackStatus,
  humanTransferRequested,
  escalationRequested,
  under30Seconds
}) => {
  const getStatusInfo = () => {
    // Check if call is resolved
    if (resolved) {
      return {
        status: 'Resolved',
        color: '#4caf50',
        bgColor: '#e8f5e8',
        icon: '✓'
      };
    }
    
    // Check for escalation (highest priority)
    if (escalationRequested) {
      return {
        status: 'Escalation',
        color: '#d32f2f',
        bgColor: '#ffebee',
        icon: '🔺'
      };
    }
    
    // Check for human transfer request
    if (humanTransferRequested) {
      return {
        status: 'Human Transfer',
        color: '#9c27b0',
        bgColor: '#f3e5f5',
        icon: '👤'
      };
    }
    
    // Check if under 30 seconds (possible disconnect/technical issue)
    if (under30Seconds) {
      return {
        status: 'Under 30s',
        color: '#ed6c02',
        bgColor: '#fff3e0',
        icon: '⚡'
      };
    }
    
    // Check if callback is requested and its status
    if (callbackRequested || callbackStatus) {
      if (callbackStatus === 'completed') {
        return {
          status: 'Callback Done',
          color: '#2196f3',
          bgColor: '#e3f2fd',
          icon: '✓'
        };
      } else if (callbackStatus === 'failed') {
        return {
          status: 'Callback Failed',
          color: '#f44336',
          bgColor: '#ffebee',
          icon: '❌'
        };
      } else {
        return {
          status: 'Callback',
          color: '#ff9800',
          bgColor: '#fff3e0',
          icon: '📞'
        };
      }
    }
    
    // Check urgency level
    if (urgencyLevel?.toLowerCase() === 'high' || urgencyLevel?.toLowerCase() === 'urgent') {
      return {
        status: 'Urgent',
        color: '#f44336',
        bgColor: '#ffebee',
        icon: '⚠️'
      };
    }
    
    // Check call status
    if (status === 'completed') {
      return {
        status: 'Completed',
        color: '#2196f3',
        bgColor: '#e3f2fd',
        icon: '✓'
      };
    }
    
    if (status === 'in_progress') {
      return {
        status: 'Active',
        color: '#9c27b0',
        bgColor: '#f3e5f5',
        icon: '🔄'
      };
    }
    
    // Default status
    return {
      status: 'Open',
      color: '#757575',
      bgColor: '#f5f5f5',
      icon: '📋'
    };
  };

  const statusInfo = getStatusInfo();

  return (
    <Chip
      label={statusInfo.status}
      size="small"
      sx={{
        backgroundColor: statusInfo.bgColor,
        color: statusInfo.color,
        fontWeight: 600,
        fontSize: '0.75rem',
        height: 24,
        border: `1px solid ${statusInfo.color}20`,
        '& .MuiChip-label': {
          px: 1.5
        }
      }}
      icon={
        <Box sx={{ fontSize: '0.75rem' }}>
          {statusInfo.icon}
        </Box>
      }
    />
  );
};

export default CallStatusBadge;

